const requestAbortControllers = new Map<string, AbortController>()

/**
 * 요청을 취소하고 맵에서 제거하는 공통 로직
 * @param key - 요청을 식별할 고유 키
 */
const abortAndCleanup = (key: string): void => {
    const controller = requestAbortControllers.get(key)
    if (controller) {
        controller.abort()
        requestAbortControllers.delete(key)
    }
}

/**
 * 여러 AbortSignal을 결합하여 하나의 신호를 생성합니다.
 * 어느 한 쪽이라도 취소되면 모두 취소됩니다.
 * @param signals - 결합할 AbortSignal 배열
 * @returns 결합된 AbortSignal
 */
const combineSignals = (...signals: AbortSignal[]): AbortSignal => {
    return AbortSignal.any(signals)
}

/**
 * @description
 * 특정 키(prefix)에 대한 진행 중인 요청의 AbortController를 관리합니다.
 * 새 요청이 들어오면 이전 요청을 취소하고, 새로운 AbortSignal을 생성하여 반환합니다.
 */
export const abortControllerManager = {
    /**
     * 새로운 AbortController를 생성하고, 이전 Controller가 있다면 취소합니다.
     * Redux Thunk의 signal과 새로운 Controller의 signal을 결합하여 반환합니다.
     * @param key - 요청을 식별할 고유 키 (예: typePrefix)
     * @param externalSignal - Redux Thunk에서 제공하는 외부 AbortSignal
     * @returns 결합된 AbortSignal
     */
    replaceSignal: (key: string, externalSignal: AbortSignal): AbortSignal => {
        // 1. 이전 요청이 있다면 취소합니다.
        abortAndCleanup(key)

        // 2. 현재 요청을 위한 새로운 AbortController를 생성하고 맵에 저장합니다.
        const newController = new AbortController()
        requestAbortControllers.set(key, newController)

        // 3. Redux Thunk의 signal과 새로운 Controller의 signal을 결합하여 반환합니다.
        return combineSignals(externalSignal, newController.signal)
    },

    /**
     * 특정 키의 요청을 취소하고 맵에서 제거합니다.
     * @param key - 요청을 식별할 고유 키 (예: typePrefix)
     */
    cancel: (key: string): void => {
        abortAndCleanup(key)
    },
}
